import { useMemo, useState } from "react";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentPasteGoIcon from "@mui/icons-material/ContentPasteGo";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { Paper, Typography } from "@mui/material";
import Badge from "@components/Badge/Badge";
import Button from "@components/Button/Button";
import PaperItem from "@components/Paper/PaperItem";
import type { DrawerType } from "@components/SideDrawer/SideDrawer.types.ts";
import { useNotification } from "@context/NotificationContext";
import {
  DEPARTURE_REJECTED_STATUS,
  DEPARTURE_STATUS,
} from "@features/vessel-call/vessel-call-status/Departure/constants";
import CallAdditionalDocumentsCard from "@features/vessel-call/vessel-call-status/VesselCallDetails/CallAdditionalDocumentsCard";
import PageLayout from "@layouts/PageLayout";
import { useDownloadCertificate } from "@pages/vessel-management/vessel-status/hooks/useDownloadCertificate";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import type { Notes } from "@services/departure/types";
import { claimDepartureRequest, unclaimDepartureRequest } from "@services/departureServiceApi";
import type { Task } from "@services/vessel-call/types";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { checkPermission } from "@utils/user-permissions";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useClearanceTypeQuery } from "../../hooks/useClearanceTypeQuery";
import ApproveSideDrawer from "../SideDrawers/ApproveSideDrawer";
import DetailsSideDrawer from "../SideDrawers/DetailsSideDrawer";
import RejectSideDrawer from "../SideDrawers/RejectSideDrawer";
import type { DepartureStatusDTO } from "./DepartureStatus.types";
import DocumentCard from "./DocumentCard";
import MarineInformation from "./MarineInformation";

export const TEST_ID_BASE = "departure-status-page";
export const LANG_BASE = "vessel-call.departure-status-page";

export const TASK_STATUS = {
  CANCEL: "CANCEL",
  READY: "READY",
  CLAIMED: "CLAIMED",
  COMPLETED: "COMPLETED",
};

type DepartureStatusProps = {
  departureDetails: DepartureStatusDTO;
  refetch: () => void;
  onResubmit: () => void;
};

const tranStyleClass = "font-normal text-[#646A77]";
const textStyleClass = "font-medium ml-4";
const wrapperFirstStyleClass = "flex gap-8";
const wrapperStyleClass = "border-t border-[#F1F2F4] pt-3 flex gap-8";

const DepartureStatus: React.FC<DepartureStatusProps> = ({
  departureDetails,
  onResubmit,
  refetch,
}) => {
  const { id: vesselCallId } = useParams();
  const { t, i18n } = useTranslation();
  const dir = i18n.language === "ar" ? "rtl" : "ltr";
  const { data: clearanceData } = useClearanceTypeQuery(vesselCallId);
  const { user, userActionPermissions } = useAuthStore();
  const { notify } = useNotification();
  const [drawerType, setDrawerType] = useState<DrawerType | null>(null);
  const {
    status,
    masterName,
    nextPort,
    finalDestination,
    vesselCondition,
    draftFore,
    draftAft,
    anchorageClearance,
    remarks,
    cargoManifest,
    presailingMasterDeclaration,
    departureCrewList,
    customsClearance,
    portClearance,
    anchorageClearanceDocument,
    additionalDocuments,
    task,
    notes,
    hasCancelledDeparture,
    requestAnchorageClearance,
  } = departureDetails;

  const hasNotes = (note?: Notes | Task): boolean => {
    if (!note) return false;
    return Boolean(
      !!note.externalNotes ||
        !!note.externalAttachmentsUrl ||
        !!note.internalNotes ||
        !!note.internalAttachmentsUrl ||
        !!note.rejectionReason ||
        !!note.rejectionAttachmentsUrl ||
        !!note.noDisputeUrl,
    );
  };

  const getRequestLabel = (clearanceType: string | undefined) => {
    if (clearanceType === "PORT_CLEARANCE") {
      return t(`${LANG_BASE}.port-clearance`);
    } else if (clearanceType === "ANCHORAGE_CLEARANCE") {
      return t(`${LANG_BASE}.anchorage-clearance`);
    }
  };

  const viewDetailsNotes =
    status !== DEPARTURE_STATUS.APPROVED_BY_MARINE_DEPARTMENT && hasNotes(notes) ? notes : task;

  const { handleDownload, handlePreview } = useDownloadCertificate();

  const taskId = task?.taskId || "";

  const badgeType = useMemo(() => {
    if (status === DEPARTURE_STATUS.APPROVED_BY_MARINE_DEPARTMENT) return "success";
    if (!!status && DEPARTURE_REJECTED_STATUS.includes(status)) return "error";
    return "warning";
  }, [status]);

  const isPortTerminal = user?.roles.includes("terminal") ?? false;
  const isShippingAgent = checkPermission(userActionPermissions, "canResubmitDeparture");

  const handleSuccess = (action: string) => {
    notify({
      title:
        action === "claim"
          ? t(`${LANG_BASE}.departure-claimed`)
          : t(`${LANG_BASE}.departure-unclaimed`),
      type: "success",
    });
    setDrawerType(null);
    refetch();
  };

  const handleError = (error: Error) => {
    return notify({
      title: t("common.error"),
      description: error?.message ?? t("common.something-went-wrong"),
      type: "error",
    });
  };

  const claimTaskMutation = useMutation({
    mutationFn: () =>
      runTransaction(
        {
          module: "vessel_call",
          screen: "DepartureStatus",
          action: "claim_departure_request",
          entityType: "departure_request",
        },
        (transactionId) => claimDepartureRequest(taskId, ...(transactionId ? [transactionId] : [])),
      ),
    onSuccess: () => handleSuccess("claim"),
    onError: (error: Error) => handleError(error),
  });

  const unclaimTaskMutation = useMutation({
    mutationFn: () =>
      runTransaction(
        {
          module: "vessel_call",
          screen: "DepartureStatus",
          action: "unclaim_departure_request",
          entityType: "departure_request",
        },
        (transactionId) =>
          unclaimDepartureRequest(taskId, ...(transactionId ? [transactionId] : [])),
      ),
    onSuccess: () => handleSuccess("unclaim"),
    onError: (error: Error) => handleError(error),
  });

  const MARINE_ROLES = ["marine-operator", "marine-supervisor"];

  const isTaskAssignedToRole = useMemo(() => {
    const taskRole = task?.role?.replace("_", "-").toLowerCase() ?? "";
    if (MARINE_ROLES.includes(taskRole)) {
      return !!user?.roles.some((r) => MARINE_ROLES.includes(r));
    }
    return !!user?.roles.includes(taskRole);
  }, [task, user]);

  const isCurrentUserOwner = useMemo(() => {
    return user?.email === task?.claimOwnerName;
  }, [task, user]);

  const isClaimButtonVisible = useMemo(() => {
    return (
      task?.status === TASK_STATUS.READY &&
      isTaskAssignedToRole &&
      !isPortTerminal &&
      !DEPARTURE_REJECTED_STATUS.includes(status ?? "")
    );
  }, [task, isTaskAssignedToRole, isPortTerminal, status]);

  const isUnclaimButtonVisible = useMemo(() => {
    return task?.status === TASK_STATUS.CLAIMED && isCurrentUserOwner && !isPortTerminal;
  }, [task, isCurrentUserOwner, isPortTerminal]);

  const isApproveButtonVisible = useMemo(() => {
    return task?.status === TASK_STATUS.CLAIMED && isCurrentUserOwner && !isPortTerminal;
  }, [task, isCurrentUserOwner, isPortTerminal]);

  const isRejectButtonVisible = useMemo(() => {
    return task?.status === TASK_STATUS.CLAIMED && isCurrentUserOwner && !isPortTerminal;
  }, [task, isCurrentUserOwner, isPortTerminal]);

  const isResubmitButtonVisible = useMemo(() => {
    return !!status && DEPARTURE_REJECTED_STATUS.includes(status) && isShippingAgent;
  }, [status, isShippingAgent]);

  const hasViewDetails = hasNotes(viewDetailsNotes);

  return (
    <PageLayout dir={dir}>
      <div className="mb-8 flex justify-between items-center">
        <div data-testid={`${TEST_ID_BASE}-title-section`}>
          <Typography variant="h5" sx={{ fontWeight: 500 }}>
            {t(`${LANG_BASE}.departure-request`)}
          </Typography>
          <div className="mt-1 flex items-center" data-testid={`${TEST_ID_BASE}-status-badge`}>
            <Badge
              type={badgeType}
              label={status ? t(`${LANG_BASE}.status-labels.${status}`) : t("common.unknown")}
            />
            {hasViewDetails && (
              <>
                <span className="text-base ml-2">–</span>
                <Button
                  testId={`${TEST_ID_BASE}-view-status-details`}
                  variant="text"
                  type="button"
                  className="ml-4 whitespace-nowrap"
                  onClick={() => setDrawerType("details")}
                  label={
                    <span className="normal-case font-medium">
                      {t(`${LANG_BASE}.view-details`)}
                    </span>
                  }
                />
              </>
            )}
          </div>
        </div>
        <div className="flex gap-4">
          {isUnclaimButtonVisible && (
            <Button
              testId={`${TEST_ID_BASE}-unclaim`}
              label={t(`${LANG_BASE}.buttons.unclaim`)}
              variant="outlined"
              onClick={() => unclaimTaskMutation.mutate()}
              sx={{ textTransform: "none" }}
            />
          )}
          {isRejectButtonVisible && (
            <Button
              testId={`${TEST_ID_BASE}-reject`}
              startIcon={<HighlightOffIcon />}
              label={t(`${LANG_BASE}.buttons.reject`)}
              variant="outlined"
              onClick={() => setDrawerType("reject")}
              sx={{ textTransform: "none" }}
            />
          )}
          {isApproveButtonVisible && (
            <Button
              testId={`${TEST_ID_BASE}-approve`}
              startIcon={<CheckCircleOutlineIcon />}
              label={t(`${LANG_BASE}.buttons.approve`)}
              variant="contained"
              onClick={() => setDrawerType("approval")}
              sx={{ textTransform: "none" }}
            />
          )}
          {isClaimButtonVisible && (
            <Button
              testId={`${TEST_ID_BASE}-claim`}
              startIcon={<ContentPasteGoIcon />}
              label={t(`${LANG_BASE}.buttons.claim`)}
              variant="contained"
              onClick={() => claimTaskMutation.mutate()}
              sx={{ textTransform: "none" }}
            />
          )}
          {isResubmitButtonVisible && (
            <Button
              testId={`${TEST_ID_BASE}-resubmit`}
              startIcon={<AutorenewIcon />}
              label={t(`${LANG_BASE}.buttons.resubmit`)}
              variant="contained"
              onClick={onResubmit}
              sx={{ textTransform: "none" }}
            />
          )}
        </div>
      </div>

      <div className="rounded-sm border border-[#E6EDF2]">
        <div className="flex items-center bg-[#F7F8FA] px-6 min-h-12 border-b border-[#E6EDF2]">
          <span className="font-bold text-base">{t(`${LANG_BASE}.departure-details`)}</span>
        </div>
        <div className="p-6">
          <Paper variant="outlined" sx={{ py: 1, px: 2 }}>
            <div data-testid={`${TEST_ID_BASE}-departure-details`}>
              <PaperItem
                testId={`${TEST_ID_BASE}-master-name`}
                translate={t(`${LANG_BASE}.master-name`)}
                tranStyle={tranStyleClass}
                text={masterName || "-"}
                textStyles={textStyleClass}
                wrapperStyle={wrapperFirstStyleClass}
              />
              <PaperItem
                testId={`${TEST_ID_BASE}-next-port`}
                translate={t(`${LANG_BASE}.next-port`)}
                tranStyle={tranStyleClass}
                text={nextPort || "-"}
                textStyles={textStyleClass}
                wrapperStyle={wrapperStyleClass}
              />
              {finalDestination && (
                <PaperItem
                  testId={`${TEST_ID_BASE}-final-desination`}
                  translate={t(`${LANG_BASE}.final-destination`)}
                  tranStyle={tranStyleClass}
                  text={finalDestination || "-"}
                  textStyles={textStyleClass}
                  wrapperStyle={wrapperStyleClass}
                />
              )}
              <PaperItem
                testId={`${TEST_ID_BASE}-vessel-condition`}
                translate={t(`${LANG_BASE}.vessel-condition`)}
                tranStyle={tranStyleClass}
                text={vesselCondition || "-"}
                textStyles={textStyleClass}
                wrapperStyle={wrapperStyleClass}
              />
              <PaperItem
                testId={`${TEST_ID_BASE}-draftFore`}
                translate={t(`${LANG_BASE}.draftFore`)}
                tranStyle={tranStyleClass}
                text={draftFore || "-"}
                textStyles={textStyleClass}
                wrapperStyle={wrapperStyleClass}
              />
              <PaperItem
                testId={`${TEST_ID_BASE}-draftAft`}
                translate={t(`${LANG_BASE}.draftAft`)}
                tranStyle={tranStyleClass}
                text={draftAft || "-"}
                textStyles={textStyleClass}
                wrapperStyle={wrapperStyleClass}
              />
              <PaperItem
                testId={`${TEST_ID_BASE}-anchorage-clearance`}
                translate={getRequestLabel(clearanceData?.clearanceType)}
                tranStyle={tranStyleClass}
                text={anchorageClearance || "-"}
                textStyles={textStyleClass}
                wrapperStyle={wrapperStyleClass}
              />
              <PaperItem
                testId={`${TEST_ID_BASE}-remarks`}
                translate={t(`${LANG_BASE}.remarks`)}
                tranStyle={tranStyleClass}
                text={remarks || "-"}
                textStyles={textStyleClass}
                wrapperStyle={wrapperStyleClass}
              />
            </div>
          </Paper>
        </div>
      </div>

      <div className="mt-6 rounded-sm border border-[#E6EDF2]">
        <div className="flex items-center bg-[#F7F8FA] px-6 min-h-12 border-b border-[#E6EDF2]">
          <span className="font-bold text-base">{t(`${LANG_BASE}.attached-documents`)}</span>
        </div>
        <div className="p-6">
          <div
            data-testid={`${TEST_ID_BASE}-attached-documents`}
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
          >
            {cargoManifest?.documentUrl && (
              <DocumentCard
                title={cargoManifest?.documentName || "-"}
                fileName={cargoManifest?.fileName || "-"}
                onDownload={() => {
                  const documentUrl = cargoManifest?.documentUrl;
                  if (!documentUrl) return;
                  handleDownload(
                    "document",
                    "bff-vessel-call",
                    documentUrl,
                    cargoManifest?.fileName || cargoManifest?.documentName,
                  );
                }}
                onPreview={() => {
                  const documentUrl = cargoManifest?.documentUrl;
                  if (!documentUrl) return;
                  handlePreview("document", "bff-vessel-call", documentUrl);
                }}
              />
            )}
            <DocumentCard
              title={presailingMasterDeclaration?.documentName || "-"}
              fileName={presailingMasterDeclaration?.fileName || "-"}
              onDownload={() => {
                const documentUrl = presailingMasterDeclaration?.documentUrl;
                if (!documentUrl) return;
                handleDownload(
                  "document",
                  "bff-vessel-call",
                  documentUrl,
                  presailingMasterDeclaration?.fileName ||
                    presailingMasterDeclaration?.documentName,
                );
              }}
              onPreview={() => {
                const documentUrl = presailingMasterDeclaration?.documentUrl;
                if (!documentUrl) return;
                handlePreview("document", "bff-vessel-call", documentUrl);
              }}
            />
            {departureCrewList?.documentUrl && (
              <DocumentCard
                title={t("vessel-call.request-departure.form-fields.departureCrewList")}
                fileName={departureCrewList.fileName || "-"}
                onDownload={() => {
                  const documentUrl = departureCrewList.documentUrl;
                  if (!documentUrl) return;
                  handleDownload(
                    "document",
                    "bff-vessel-call",
                    documentUrl,
                    departureCrewList.fileName || departureCrewList.documentName,
                  );
                }}
                onPreview={() => {
                  const documentUrl = departureCrewList.documentUrl;
                  if (!documentUrl) return;
                  handlePreview("document", "bff-vessel-call", documentUrl);
                }}
              />
            )}
            {customsClearance?.documentUrl && (
              <DocumentCard
                title={t(`${LANG_BASE}.customs-clearance`)}
                fileName={"customs_clearance.pdf"}
                onDownload={() => {
                  const documentUrl = customsClearance?.documentUrl;
                  if (!documentUrl) return;
                  handleDownload(
                    "document",
                    "bff-vessel-call",
                    documentUrl,
                    customsClearance?.fileName || customsClearance?.documentName,
                  );
                }}
                onPreview={() => {
                  const documentUrl = customsClearance?.documentUrl;
                  if (!documentUrl) return;
                  handlePreview("document", "bff-vessel-call", documentUrl);
                }}
              />
            )}
            {portClearance?.documentUrl && (
              <DocumentCard
                title={t(`${LANG_BASE}.port-clearance`)}
                fileName={portClearance?.fileName || "-"}
                onDownload={() =>
                  handleDownload(
                    "document",
                    "bff-vessel-call",
                    portClearance?.documentUrl,
                    portClearance?.fileName || portClearance?.documentName,
                  )
                }
                onPreview={() => {
                  const documentUrl = portClearance?.documentUrl;
                  if (!documentUrl) return;
                  handlePreview("document", "bff-vessel-call", documentUrl);
                }}
              />
            )}
            {anchorageClearanceDocument?.documentUrl && (
              <DocumentCard
                title={t(`${LANG_BASE}.anchorage-clearance`)}
                fileName={anchorageClearanceDocument?.fileName || "-"}
                onDownload={() =>
                  handleDownload(
                    "document",
                    "bff-vessel-call",
                    anchorageClearanceDocument?.documentUrl,
                    anchorageClearanceDocument?.fileName ||
                      anchorageClearanceDocument?.documentName,
                  )
                }
                onPreview={() => {
                  const documentUrl = anchorageClearanceDocument?.documentUrl;
                  if (!documentUrl) return;
                  handlePreview("document", "bff-vessel-call", documentUrl);
                }}
              />
            )}
          </div>
        </div>
      </div>

      <MarineInformation marineInformation={departureDetails.marineInformation} />

      {additionalDocuments && additionalDocuments.length > 0 && (
        <div className="mt-6 rounded-sm border border-[#E6EDF2]">
          <div className="flex items-center bg-[#F7F8FA] px-6 min-h-12 border-b border-[#E6EDF2]">
            <span className="font-bold text-base">{t(`${LANG_BASE}.additional-documents`)}</span>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(additionalDocuments ?? []).map((document, idx) => (
                <CallAdditionalDocumentsCard
                  key={`additional-document-${idx}`}
                  testId={`${TEST_ID_BASE}-additional-document-${idx}`}
                  documentName={document.documentName || t(`${LANG_BASE}.additional-documents`)}
                  fileName={document.fileName || ""}
                  url={document.documentUrl}
                  documentType={document.documentType}
                  otherSpecifyType={document.otherSpecifyType}
                  issueDate={document.issueDate ?? undefined}
                  expiryDate={document.expiryDate ?? undefined}
                  documentSummary={document.documentSummary}
                  onDownload={() => {
                    if (!document.documentUrl) return;
                    handleDownload(
                      "document",
                      "bff-vessel-call",
                      document.documentUrl,
                      document.fileName || document.documentName,
                    );
                  }}
                  onPreview={() => {
                    if (!document.documentUrl) return;
                    handlePreview("document", "bff-vessel-call", document.documentUrl);
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      )}
      <ApproveSideDrawer
        isOpen={drawerType === "approval"}
        taskId={taskId}
        onClose={() => setDrawerType(null)}
        onSuccess={refetch}
        onError={refetch}
        hasCancelledDeparture={hasCancelledDeparture}
        requestAnchorageClearance={requestAnchorageClearance}
      />
      <RejectSideDrawer
        isOpen={drawerType === "reject"}
        taskId={taskId}
        onClose={() => setDrawerType(null)}
        onSuccess={refetch}
        onError={refetch}
      />
      <DetailsSideDrawer
        isOpen={drawerType === "details"}
        onClose={() => setDrawerType(null)}
        rejectionReason={viewDetailsNotes?.rejectionReason ?? undefined}
        rejectionDocument={viewDetailsNotes?.rejectionAttachmentsUrl ?? undefined}
        rejectionDocumentName={viewDetailsNotes?.rejectionAttachmentsName ?? undefined}
        internalNotes={viewDetailsNotes?.internalNotes ?? undefined}
        internalDocument={viewDetailsNotes?.internalAttachmentsUrl ?? undefined}
        internalDocumentName={viewDetailsNotes?.internalAttachmentsName ?? undefined}
        externalNotes={viewDetailsNotes?.externalNotes ?? undefined}
        externalDocument={viewDetailsNotes?.externalAttachmentsUrl ?? undefined}
        externalDocumentName={viewDetailsNotes?.externalAttachmentsName ?? undefined}
        noDisputeDocument={viewDetailsNotes?.noDisputeUrl ?? undefined}
        noDisputeDocumentName={viewDetailsNotes?.noDisputeName ?? undefined}
        isDispute={status === DEPARTURE_STATUS.REJECTED_BY_PORT_TERMINAL}
        submittedBy={departureDetails.notes?.user ?? undefined}
        submittedByRole={departureDetails.notes?.role ?? undefined}
        submittedAt={departureDetails.notes?.createdAt ?? undefined}
      />
    </PageLayout>
  );
};

export default DepartureStatus;
